import React, { useEffect, useRef, useState } from 'react';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import logo from '../../assets/ciclii3.png';
import Loader from '../../components/Loader';
import { useAuth } from '../../context/AuthContext';
import { routes } from '../../routes/routeConfig';
import { isValidEmailWithCommonTLD } from '../../utils/emailValidation';

const LandingPage = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [errorResponse, setErrorResponse] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [passwordError, setPasswordError] = useState('');
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [emailError, setEmailError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [allValidate, setAllValidate] = useState(false);
    const [registerRedirect, setRegisterRedirect] = useState(false);
    const redirectTimeRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const navigate = useNavigate();
    const { login } = useAuth();

    // Validación de un solo campo (idéntica a register)
    const validateField = (field: string, value: string) => {
        let error = '';
        switch (field) {
            case 'email': {
                if (!value || !isValidEmailWithCommonTLD(value)) {
                    error = 'Correo electrónico no válido';
                }
                break;
            }
            case 'password': {
                if (!value || value.length < 8) {
                    error = 'La contraseña debe tener al menos 8 caracteres';
                } else if (!value || value.length > 25) {
                    error = 'La contraseña no puede exceder los 25 caracteres';
                }
                break;
            }
            default:
                break;
        }
        setErrors(prev => {
            const updated = { ...prev, [field]: error };
            // Si no hay ningún error en el formulario, allValidate es true
            const hasAnyError = Object.values(updated).some(e => e);
            setAllValidate(!hasAnyError);
            return updated;
        });
        return error === '';
    };



    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type } = e.target;
        let fieldValue = value.trim();
        if (name === 'email' || name === 'password') {
            if (name === 'email') setEmail(fieldValue.toLocaleLowerCase().trim());
            if (name === 'password') setPassword(fieldValue);
            validateField(name, fieldValue);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setErrorResponse('');
        setIsLoading(true);

        if (!allValidate) {
            setErrorResponse('Por favor completa todos los campos correctamente');
            setIsLoading(false);
            return;
        }

        try {
            const res = await login(email, password);

            if (res.success) {
                navigate(routes.redirects.afterLogin);
            } else {
                if (res.statusCode === 404) {
                    setRegisterRedirect(true);
                    redirectTimeRef.current = setTimeout(() => {
                        navigate(routes.public.register);
                    }, 1000);
                }
                setErrorResponse(res.message || 'Email o contraseña incorrectos');
            }
        } catch (err) {
            setErrorResponse('Error al conectar con el servidor. Intenta nuevamente.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        return () => {
            if (redirectTimeRef.current) {
                clearTimeout(redirectTimeRef.current);
            }
        };
    }, []);

    if (registerRedirect) {
        return <div className='logout-overlay'>
            <Loader text='Regístrate' />
        </div>;
    }

    return (
        <div className='landing-container'>
            <div className='landing-content'>
                <div className='landing-hero'>
                    <h1>Bienvenida a</h1>
                    <img src={logo} alt='CICLII Logo' className='' />
                    <p>Tu compañera digital para entender y gestionar tu ciclo menstrual</p>
                </div>

                <div className='login-card'>
                    <h2>Iniciar Sesión</h2>
                    <form onSubmit={handleSubmit}>

                        <div className='form-group'>
                            <label htmlFor='email'>Email</label>
                            <input
                                className='input'
                                type='email'
                                id='email'
                                name='email'
                                value={email}
                                onChange={handleChange}
                                placeholder='tu@email.com'
                                disabled={isLoading}
                            />
                            {errors.email && <div className='error-message'>{errors.email}</div>}
                        </div>

                        <div className='form-group'>
                            <label htmlFor='password'>Contraseña</label>
                            <div style={{ position: 'relative' }}>
                                <input
                                    className='input'
                                    type={showPassword ? 'text' : 'password'}
                                    id='password'
                                    name='password'
                                    value={password}
                                    onChange={handleChange}
                                    placeholder='••••••••'
                                    disabled={isLoading}
                                    maxLength={26}
                                    style={{ paddingRight: '40px' }}
                                />
                                <button
                                    type='button'
                                    tabIndex={-1}
                                    aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                                    onClick={() => setShowPassword(v => !v)}
                                    style={{
                                        position: 'absolute',
                                        right: '12px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        padding: 0,
                                        color: '#B57EDC',
                                        fontSize: 20,
                                        zIndex: 2,
                                    }}
                                >
                                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                                </button>
                            </div>
                            {errors.password && <div className='error-message'>{errors.password}</div>}
                        </div>

                        {errorResponse && <div className='error-message'>{errorResponse}</div>}

                        <button type='submit' className='shared-btn' disabled={isLoading || !allValidate}>
                            {isLoading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
                        </button>
                    </form>

                    <p className='signup-link'>
                        ¿No tienes cuenta? <button onClick={() => navigate('/registro')}>Regístrate aquí</button>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default LandingPage;